import React, { useMemo, useCallback, useState } from "react";
import { Tabs, Card } from "antd";
import { HourglassEmpty, Assignment, CheckCircle } from "@mui/icons-material";
import PropTypes from "prop-types"; // Importar PropTypes

const AutorizacionesTabs = React.memo(({ autorizaciones }) => {
  const [activeTab, setActiveTab] = useState("1");
  // Memoizar función de renderizado de cards para optimizar performance
  const renderAutorizacionCard = useCallback((autorizacion) => (
    <Card
      style={{ marginBottom: 10 }}
      key={autorizacion.id}
      title={`Autorización: ${
        autorizacion.numero_autorizacion || autorizacion.numero_pre_autorizacion
      }`}
    >
      <p>
        <strong>Paciente:</strong> {autorizacion.nombre_paciente}
      </p>
      <p>
        <strong>Número de Documento:</strong>{" "}
        {autorizacion.numero_documento_paciente}
      </p>
      <p>
        <strong>Autorización:</strong>{" "}
        {autorizacion.numero_autorizacion ||
          autorizacion.numero_pre_autorizacion}
      </p>
    </Card>
  ), []);

  // Memoizar clasificaciones para evitar recálculos innecesarios
  const autorizacionesCategorizadas = useMemo(() => {
    const sinGestionar = [];
    const gestionadasIncompletas = [];
    const gestionadasCompletadas = [];

    autorizaciones.forEach((autorizacion) => {
      switch (autorizacion.estado_traslado) {
        case "Traslados Sin Gestionar":
          sinGestionar.push(autorizacion);
          break;
        case "Traslados Gestionados Incompletos":
          gestionadasIncompletas.push(autorizacion);
          break;
        case "Traslados Gestionados Completados":
          gestionadasCompletadas.push(autorizacion);
          break;
        default:
          break;
      }
    });

    return {
      sinGestionar,
      gestionadasIncompletas,
      gestionadasCompletadas
    };
  }, [autorizaciones]);

  const { sinGestionar: autorizacionesSinGestionar, 
          gestionadasIncompletas: autorizacionesGestionadasIncompletas,
          gestionadasCompletadas: autorizacionesGestionadasCompletadas } = autorizacionesCategorizadas;

  // Función para renderizar contenido lazy
  const renderTabContent = useCallback((tabKey, autorizacionesList, emptyMessage) => {
    // Solo renderizar si es el tab activo (lazy loading)
    if (tabKey !== activeTab) {
      return <div>Loading...</div>;
    }

    if (autorizacionesList.length > 0) {
      return autorizacionesList.map((autorizacion) =>
        renderAutorizacionCard(autorizacion)
      );
    }
    
    return <p>{emptyMessage}</p>;
  }, [activeTab, renderAutorizacionCard]);

  // Memoizar configuración de tabs
  const tabItems = useMemo(() => [
    {
      key: "1",
      label: (
        <span style={{ display: "flex", alignItems: "center" }}>
          <HourglassEmpty style={{ fontSize: 20, marginRight: 5 }} />
          Sin Gestionar ({autorizacionesSinGestionar.length})
        </span>
      ),
      children: renderTabContent("1", autorizacionesSinGestionar, "No hay autorizaciones sin gestionar."),
    },
    {
      key: "2",
      label: (
        <span style={{ display: "flex", alignItems: "center" }}>
          <Assignment style={{ fontSize: 20, marginRight: 5 }} />
          Gestionadas Incompletas ({autorizacionesGestionadasIncompletas.length})
        </span>
      ),
      children: renderTabContent("2", autorizacionesGestionadasIncompletas, "No hay autorizaciones gestionadas incompletas."),
    },
    {
      key: "3",
      label: (
        <span style={{ display: "flex", alignItems: "center" }}>
          <CheckCircle style={{ fontSize: 20, marginRight: 5 }} />
          Gestionadas Completadas ({autorizacionesGestionadasCompletadas.length})
        </span>
      ),
      children: renderTabContent("3", autorizacionesGestionadasCompletadas, "No hay autorizaciones gestionadas completadas."),
    },
  ], [
    autorizacionesSinGestionar,
    autorizacionesGestionadasIncompletas, 
    autorizacionesGestionadasCompletadas,
    renderTabContent
  ]);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
  }, []);

  return (
    <Tabs 
      defaultActiveKey="1" 
      centered 
      items={tabItems} 
      onChange={handleTabChange}
    />
  );
});

// Validación de las props con PropTypes
AutorizacionesTabs.propTypes = {
  autorizaciones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre_paciente: PropTypes.string.isRequired,
      numero_documento_paciente: PropTypes.string.isRequired,
      numero_autorizacion: PropTypes.string,
      numero_pre_autorizacion: PropTypes.string,
      estado_traslado: PropTypes.string.isRequired,
    })
  ).isRequired, // autorizaciones es obligatorio y debe ser un array de objetos con esta estructura
};

export default AutorizacionesTabs;
